import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { format, parseISO, addMinutes } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { Calendar as CalendarIcon, User, Users, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

import { staffService, type StaffBooking } from '@/services/api/staff.service';
import { useAuth } from '@/contexts/auth-context';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SearchBar } from '@/components/ui/search-bar';
import { StatusBadge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { StatusFilterChip } from '@/components/ui/status-filter-chip';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { type BookingStatus } from '@/lib/status-colors';
import { Calendar as DatePicker } from '@/components/ui/calendar';
import { Calendar } from '@/components/calendar/Calendar';
import type { CalendarEvent, EventStatus } from '@/lib/calendar/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { Spinner } from '@/components/ui/spinner';
import { BookingActions } from '@/components/staff/BookingActions';
import { sanitizeErrorMessage } from '@/lib/error-utils';

const BOOKING_STATUS_TO_EVENT: Record<BookingStatus, EventStatus> = {
  pending: 'pending',
  confirmed: 'confirmed',
  completed: 'done',
  cancelled: 'cancelled',
  no_show: 'blocked',
};

const STATUS_ORDER = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'] as const;

export default function StaffBookingsPage() {
  const { t } = useTranslation(['staff', 'common']);
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'day' | 'week' | 'month'>('list');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const perPage = 10;

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useSetPageHeader({ title: t('bookings.title'), sub: t('bookings.subtitle') }, [t]);

  const statusLabel = (status: string) =>
    t(`staff:status.${status === 'no_show' ? 'noShow' : status}`, status.replace('_', ' '));

  const matchingBookings = (bookings: StaffBooking[], query: string): StaffBooking[] => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return bookings.filter(b =>
      (b.customer_full_name || '').toLowerCase().includes(lower) ||
      (b.customer_email || '').toLowerCase().includes(lower)
    );
  };

  const renderGroupSubtitle = (
    entry: { participantCount: number; bookings: StaffBooking[] },
    options: { showCountWhenNoMatch?: boolean } = {}
  ) => {
    const matched = matchingBookings(entry.bookings, debouncedSearch);
    if (matched.length === 0) {
      if (!options.showCountWhenNoMatch) return null;
      return (
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {t('bookings.list.participantCount', { count: entry.participantCount, defaultValue: '{{count}} participants' })}
        </p>
      );
    }
    const shown = matched.slice(0, 2);
    const others = Math.max(0, entry.participantCount - shown.length);
    return (
      <div className="text-xs space-y-0.5 mt-0.5">
        {shown.map(b => (
          <p key={b.id} className="truncate">
            <span className="font-medium text-slate-700 dark:text-slate-300">{b.customer_full_name}</span>
            <span className="text-slate-500 dark:text-slate-400"> · {b.customer_email}</span>
          </p>
        ))}
        {others > 0 && (
          <p className="text-slate-500 dark:text-slate-400">
            {t('bookings.list.otherParticipants', { count: others, defaultValue: '+ {{count}} other participants' })}
          </p>
        )}
      </div>
    );
  };

  // Admins without a provider profile can reach this route directly even though
  // the sidebar link is hidden. Skip the query (which would 403) and render a
  // friendly empty state below.
  const hasProviderProfile = user?.hasProviderProfile !== false;

  // Debounce the search input so we don't fire a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Fetch bookings with filters and pagination. Calendar views need the full
  // visible range (incl. past) so group classes don't disappear off-page.
  const isCalendarView = viewMode !== 'list';
  const { data: bookingsResponse, isLoading, error, isError } = useQuery({
    queryKey: ['staff-bookings', statusFilter, debouncedSearch, currentPage, perPage, isCalendarView],
    queryFn: () => staffService.getBookings({
      ...(statusFilter !== 'all' ? { status: statusFilter as any } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      page: isCalendarView ? 1 : currentPage,
      per_page: isCalendarView ? 500 : perPage,
    }),
    enabled: hasProviderProfile,
    placeholderData: keepPreviousData,
  });

  const bookings = bookingsResponse?.data || [];
  const pagination = bookingsResponse?.meta;

  // Fetch all bookings for status counts (without pagination)
  const { data: allBookingsData } = useQuery({
    queryKey: ['staff-bookings-counts'],
    queryFn: () => staffService.getBookings({ per_page: 1000 }),
    enabled: hasProviderProfile,
  });

  const allBookings = allBookingsData?.data || [];

  // Search is now applied server-side via debouncedSearch, so use the response directly.
  const filteredBookings = bookings;

  // Group bookings by status for count cards
  const groupBookingsByStatus = () => {
    return allBookings.reduce((groups, booking) => {
      const status = booking.status;
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(booking);
      return groups;
    }, {} as Record<string, StaffBooking[]>);
  };

  const bookingGroups = groupBookingsByStatus();

  // Handle date picker selection - jump to day view
  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setViewMode('day');
      setIsCalendarOpen(false);
    }
  };

  // Build CalendarEvents from bookings for the unified Calendar component.
  // Group-class bookings collapse into a single event per available_slot_id so
  // the calendar shows "Pilates Class · 3/6" instead of stacking N participants.
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    const events: CalendarEvent[] = [];
    const groupBuckets = new Map<number, StaffBooking[]>();

    for (const booking of filteredBookings) {
      if (!booking.start_time) continue;
      const isGroupClass = (booking.service?.max_capacity ?? 1) > 1 && booking.available_slot_id != null;
      if (isGroupClass) {
        const list = groupBuckets.get(booking.available_slot_id!) ?? [];
        list.push(booking);
        groupBuckets.set(booking.available_slot_id!, list);
        continue;
      }
      const start = parseISO(booking.start_time);
      const end = booking.end_time
        ? parseISO(booking.end_time)
        : addMinutes(start, booking.service?.duration_minutes ?? 30);
      events.push({
        id: String(booking.id),
        start,
        end,
        title: booking.customer_first_name ?? '',
        subtitle: booking.service?.name || booking.service_name,
        status: BOOKING_STATUS_TO_EVENT[booking.status as BookingStatus] ?? 'confirmed',
        meta: { booking },
      });
    }

    for (const [slotId, bs] of groupBuckets.entries()) {
      const first = bs[0];
      if (!first.start_time) continue;
      const start = parseISO(first.start_time);
      const end = first.end_time
        ? parseISO(first.end_time)
        : addMinutes(start, first.service?.duration_minutes ?? 30);
      const cap = first.service?.max_capacity ?? bs.length;
      const slotCurrent = first.available_slot_current_bookings;
      const count = typeof slotCurrent === 'number' ? slotCurrent : bs.length;
      const serviceName = first.service?.name || first.service_name || t('bookings.list.groupBooking', 'Group Booking');
      events.push({
        id: `group-${slotId}`,
        start,
        end,
        title: serviceName,
        subtitle: `${count}/${cap}`,
        status: 'group-class',
        meta: { groupSlotId: slotId },
      });
    }

    return events;
  }, [filteredBookings, t]);

  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Sort bookings by date
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const dateA = a.start_time ? new Date(a.start_time).getTime() : 0;
    const dateB = b.start_time ? new Date(b.start_time).getTime() : 0;
    return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Collapse group-class bookings into one entry per available_slot_id.
  // 1:1 bookings render unchanged; group bookings show as a single "Group Booking"
  // row/card with a participant count, linking to /staff/group-classes/:slotId.
  type ListEntry =
    | { kind: 'individual'; booking: StaffBooking }
    | {
        kind: 'group';
        slotId: number;
        serviceName: string;
        startTime: string;
        participantCount: number;
        maxCapacity: number;
        statusCounts: Record<string, number>;
        bookings: StaffBooking[];
      };

  const listEntries = useMemo<ListEntry[]>(() => {
    const groups = new Map<number, StaffBooking[]>();
    const individuals: StaffBooking[] = [];

    for (const b of sortedBookings) {
      const isGroupClass = (b.service?.max_capacity ?? 1) > 1 && b.available_slot_id != null;
      if (isGroupClass) {
        const list = groups.get(b.available_slot_id!) ?? [];
        list.push(b);
        groups.set(b.available_slot_id!, list);
      } else {
        individuals.push(b);
      }
    }

    const groupEntries: ListEntry[] = Array.from(groups.entries()).map(([slotId, bs]) => {
      const statusCounts: Record<string, number> = {};
      for (const b of bs) {
        statusCounts[b.status] = (statusCounts[b.status] || 0) + 1;
      }
      const slotCurrent = bs[0].available_slot_current_bookings;
      return {
        kind: 'group',
        slotId,
        serviceName: bs[0].service?.name ?? bs[0].service_name ?? '',
        startTime: bs[0].start_time ?? '',
        participantCount: typeof slotCurrent === 'number' ? slotCurrent : bs.length,
        maxCapacity: bs[0].service?.max_capacity ?? bs.length,
        statusCounts,
        bookings: bs,
      };
    });

    const individualEntries: ListEntry[] = individuals.map((booking) => ({ kind: 'individual', booking }));

    return [...groupEntries, ...individualEntries].sort((a, b) => {
      const ta = a.kind === 'group' ? a.startTime : a.booking.start_time ?? '';
      const tb = b.kind === 'group' ? b.startTime : b.booking.start_time ?? '';
      return sortDirection === 'asc' ? ta.localeCompare(tb) : tb.localeCompare(ta);
    });
  }, [sortedBookings, sortDirection]);

  // Mutations
  const confirmMutation = useMutation({
    mutationFn: (id: number) => staffService.confirmBooking(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['staff-bookings-counts'] });
      toast({
        title: t('common:toasts.success'),
        description: t('bookings.actions.confirmSuccess'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: sanitizeErrorMessage(error, t('bookings.actions.confirmError')),
        variant: 'destructive',
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      staffService.cancelBooking(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['staff-bookings-counts'] });
      toast({
        title: t('common:toasts.success'),
        description: t('bookings.actions.cancelSuccess'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: sanitizeErrorMessage(error, t('bookings.actions.cancelError')),
        variant: 'destructive',
      });
    },
  });

  const noShowMutation = useMutation({
    mutationFn: (id: number) => staffService.markNoShow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['staff-bookings-counts'] });
      toast({
        title: t('common:toasts.success'),
        description: t('bookings.actions.noShowSuccess'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: sanitizeErrorMessage(error, t('bookings.actions.noShowError')),
        variant: 'destructive',
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: number) => staffService.completeBooking(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['staff-bookings-counts'] });
      toast({
        title: t('common:toasts.success'),
        description: t('bookings.actions.completeSuccess'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: sanitizeErrorMessage(error, t('bookings.actions.completeError')),
        variant: 'destructive',
      });
    },
  });

  const handleView = (booking: StaffBooking) => {
    setLocation(`/staff/bookings/${booking.public_id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!hasProviderProfile) {
    return (
      <div className="p-8">
        <div className="max-w-2xl mx-auto bg-surface border border-line rounded-lg p-8 text-center">
          <User className="h-12 w-12 mx-auto text-soft mb-4" />
          <h2 className="text-h2 text-ink mb-2">
            {t('bookings.noProviderProfile.title', 'No provider profile')}
          </h2>
          <p className="text-soft mb-6">
            {t(
              'bookings.noProviderProfile.description',
              'Your account is not linked to a provider, so personal bookings are not available. Use Team Bookings to manage your organization’s schedule.',
            )}
          </p>
          <Button onClick={() => setLocation('/admin/bookings')}>
            {t('bookings.noProviderProfile.cta', 'Go to Team Bookings')}
          </Button>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8">
        <Alert variant="destructive" className="max-w-2xl mx-auto">
          <AlertTitle>{t('staff:bookings.errorLoadingTitle')}</AlertTitle>
          <AlertDescription>{sanitizeErrorMessage(error, 'Unknown error occurred')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Combined Toolbar: Search + Calendar + View Mode + Status chips */}
      <div className="rounded-xl p-4 border border-line bg-surface space-y-4">
        <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
        <SearchBar
          containerClassName="flex-1"
          placeholder={t('bookings.search.placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-bookings"
          actions={
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('bookings.actions.goToDate')}
                  title={t('bookings.actions.goToDate')}
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <DatePicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleCalendarSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          }
        />

        {/* Right side: View Mode Switcher */}
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'day' | 'week' | 'month')}>
          <TabsList>
            <TabsTrigger value="list">{t('bookings.viewModes.list')}</TabsTrigger>
            <TabsTrigger value="day">{t('bookings.viewModes.day')}</TabsTrigger>
            <TabsTrigger value="week">{t('bookings.viewModes.week')}</TabsTrigger>
            <TabsTrigger value="month">{t('bookings.viewModes.month')}</TabsTrigger>
          </TabsList>
        </Tabs>
        </div>

        {/* Status filter chips */}
        <div
          role="group"
          aria-label={t('bookings.filters.allStatuses')}
          className="flex flex-wrap items-center gap-2"
        >
          <StatusFilterChip
            status="all"
            active={statusFilter === 'all'}
            label={t('bookings.filters.allStatuses')}
            count={allBookings.length}
            onClick={() => setStatusFilter('all')}
          />
          {(['pending', 'confirmed', 'completed', 'cancelled', 'no_show'] as const).map((status) => (
            <StatusFilterChip
              key={status}
              status={status}
              active={statusFilter === status}
              label={t(`bookings.statusCounts.${status === 'no_show' ? 'noShow' : status}`)}
              count={bookingGroups[status]?.length || 0}
              onClick={() => setStatusFilter(status)}
            />
          ))}
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {/* Desktop / tablet: table layout */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-2">
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap">
                      {t('bookings.table.customer')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                      {t('bookings.table.status')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                      {t('bookings.table.participants', 'Participants')}
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap"
                      aria-sort={sortDirection === 'asc' ? 'ascending' : 'descending'}
                    >
                      <button
                        type="button"
                        onClick={toggleSortDirection}
                        className="inline-flex items-center gap-1.5 hover:text-ink focus:outline-none focus:ring-2 focus:ring-brand-500 rounded"
                      >
                        {t('bookings.table.date')}
                        {sortDirection === 'asc' ? (
                          <ArrowUp className="h-3.5 w-3.5" />
                        ) : (
                          <ArrowDown className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </th>
                    <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden lg:table-cell">
                      {t('bookings.table.service')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-right">
                      <span className="sr-only">{t('bookings.table.actions')}</span>
                    </th>
                  </tr>
                </thead>

                {listEntries.length === 0 ? (
                  <tbody>
                    <tr>
                      <td colSpan={5} className="px-4 py-16 text-center">
                        <CalendarIcon className="h-10 w-10 mx-auto mb-3 text-soft" />
                        <p className="text-soft font-medium">
                          {t('bookings.list.noBookings')}
                        </p>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {listEntries.map((entry) => entry.kind === 'group' ? (
                      <tr
                        key={`group-${entry.slotId}`}
                        onClick={() => setLocation(`/staff/group-classes/${entry.slotId}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setLocation(`/staff/group-classes/${entry.slotId}`);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-violet-50 dark:hover:bg-violet-900/20 cursor-pointer transition-colors focus:outline-none focus:bg-violet-50 dark:focus:bg-violet-900/20"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                              <Users className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white truncate">
                                {t('bookings.list.groupBooking', 'Group Booking')}
                              </p>
                              {renderGroupSubtitle(entry, { showCountWhenNoMatch: true })}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {STATUS_ORDER.map((status) => {
                              const count = entry.statusCounts[status] || 0;
                              if (count === 0) return null;
                              return (
                                <StatusBadge key={status} status={status as BookingStatus} size="sm">
                                  {count} {statusLabel(status)}
                                </StatusBadge>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                            <Users className="h-3.5 w-3.5 text-slate-400" />
                            <span className="tabular-nums">{entry.participantCount}/{entry.maxCapacity}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                          {entry.startTime ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-slate-900 dark:text-white">
                                {format(parseISO(entry.startTime), getDateFormats().medium, { locale: getDateLocale() })}
                              </span>
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                {format(parseISO(entry.startTime), 'HH:mm')}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300 hidden lg:table-cell">
                          {entry.serviceName || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-violet-700 dark:text-violet-300">
                            {t('bookings.list.viewClass', 'View class')}
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </td>
                      </tr>
                    ) : (
                      <tr
                        key={`booking-${entry.booking.id}`}
                        onClick={() => handleView(entry.booking)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleView(entry.booking);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        className="border-b border-line last:border-0 hover:bg-surface-2 cursor-pointer transition-colors focus:outline-none focus:bg-surface-2"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex-shrink-0 h-9 w-9 rounded-full bg-surface-2 flex items-center justify-center">
                              <User className="h-4 w-4 text-soft" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-ink truncate">
                                {entry.booking.customer_full_name}
                              </p>
                              <p className="text-xs text-soft truncate">
                                {entry.booking.customer_email}
                              </p>
                              <div className="md:hidden mt-1">
                                <StatusBadge status={entry.booking.status as BookingStatus} size="sm">
                                  {statusLabel(entry.booking.status)}
                                </StatusBadge>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <StatusBadge status={entry.booking.status as BookingStatus}>
                            {statusLabel(entry.booking.status)}
                          </StatusBadge>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300 tabular-nums">1</span>
                        </td>
                        <td className="px-4 py-3 text-ink whitespace-nowrap">
                          {entry.booking.start_time ? (
                            <div className="flex flex-col">
                              <span className="font-medium text-ink">
                                {format(parseISO(entry.booking.start_time), getDateFormats().medium, { locale: getDateLocale() })}
                              </span>
                              <span className="text-xs text-soft">
                                {format(parseISO(entry.booking.start_time), 'HH:mm')}
                              </span>
                            </div>
                          ) : (
                            <span className="text-soft">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ink hidden lg:table-cell">
                          {entry.booking.service?.name || entry.booking.service_name || (
                            <span className="text-soft">—</span>
                          )}
                        </td>
                        <td
                          className="px-4 py-3 text-right whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-1.5">
                            <BookingActions
                              bookingId={entry.booking.id}
                              status={entry.booking.status as 'pending' | 'confirmed' | 'cancelled' | 'no_show' | 'completed'}
                              startTime={entry.booking.start_time || ''}
                              onConfirm={(id) => confirmMutation.mutate(id) as any}
                              onCancel={(id, reason) => cancelMutation.mutate({ id, reason }) as any}
                              onNoShow={(id) => noShowMutation.mutate(id) as any}
                              onComplete={(id) => completeMutation.mutate(id) as any}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>

            {/* Mobile: card layout */}
            <div className="md:hidden">
              {listEntries.length === 0 ? (
                <div className="px-4 py-16 text-center">
                  <CalendarIcon className="h-10 w-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                  <p className="text-slate-500 dark:text-slate-400 font-medium">
                    {t('bookings.list.noBookings')}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {listEntries.map((entry) => entry.kind === 'group' ? (
                    <li
                      key={`group-card-${entry.slotId}`}
                      onClick={() => setLocation(`/staff/group-classes/${entry.slotId}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setLocation(`/staff/group-classes/${entry.slotId}`);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      className="px-4 py-3 hover:bg-violet-50 dark:hover:bg-violet-900/20 active:bg-violet-100 cursor-pointer transition-colors focus:outline-none focus:bg-violet-50"
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                          <Users className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="font-medium text-slate-900 dark:text-white truncate">
                              {entry.serviceName || t('bookings.list.groupBooking', 'Group Booking')}
                            </p>
                            <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                            <Users className="h-3 w-3 text-slate-400" />
                            <span className="tabular-nums">{entry.participantCount}/{entry.maxCapacity}</span>
                          </p>
                          {renderGroupSubtitle(entry)}
                          {entry.startTime && (
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                              {format(parseISO(entry.startTime), getDateFormats().medium, { locale: getDateLocale() })}
                              {' · '}
                              {format(parseISO(entry.startTime), 'HH:mm')}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {STATUS_ORDER.map((status) => {
                              const count = entry.statusCounts[status] || 0;
                              if (count === 0) return null;
                              return (
                                <StatusBadge key={status} status={status as BookingStatus} size="sm">
                                  {count} {statusLabel(status)}
                                </StatusBadge>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </li>
                  ) : (
                    <li
                      key={`booking-card-${entry.booking.id}`}
                      onClick={() => handleView(entry.booking)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleView(entry.booking);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      className="px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 active:bg-slate-100 cursor-pointer transition-colors focus:outline-none focus:bg-slate-50"
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                          <User className="h-5 w-5 text-slate-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 dark:text-white truncate">
                            {entry.booking.customer_full_name}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {entry.booking.customer_email}
                          </p>
                          {entry.booking.start_time && (
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                              {format(parseISO(entry.booking.start_time), getDateFormats().medium, { locale: getDateLocale() })}
                              {' · '}
                              {format(parseISO(entry.booking.start_time), 'HH:mm')}
                            </p>
                          )}
                          <div className="mt-1.5">
                            <StatusBadge status={entry.booking.status as BookingStatus} size="sm">
                              {statusLabel(entry.booking.status)}
                            </StatusBadge>
                          </div>
                        </div>
                      </div>
                      <div
                        className="mt-2 flex justify-end"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <BookingActions
                          bookingId={entry.booking.id}
                          status={entry.booking.status as 'pending' | 'confirmed' | 'cancelled' | 'no_show' | 'completed'}
                          startTime={entry.booking.start_time || ''}
                          onConfirm={(id) => confirmMutation.mutate(id) as any}
                          onCancel={(id, reason) => cancelMutation.mutate({ id, reason }) as any}
                          onNoShow={(id) => noShowMutation.mutate(id) as any}
                          onComplete={(id) => completeMutation.mutate(id) as any}
                          compact
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
          {/* Pagination Controls */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t">
              <div className="text-sm text-soft">
                {t('bookings.pagination.showing', {
                  start: (pagination.page - 1) * perPage + 1,
                  end: Math.min(pagination.page * perPage, pagination.total_count),
                  total: pagination.total_count
                })}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={pagination.page === 1}
                  className="min-h-[36px]"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="ml-1">{t('bookings.pagination.previous')}</span>
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={pagination.page === pageNum ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="min-h-[36px] min-w-[36px]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(pagination.total_pages, p + 1))}
                  disabled={pagination.page === pagination.total_pages}
                  className="min-h-[36px]"
                >
                  <span className="mr-1">{t('bookings.pagination.next')}</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Day / Week / Month — unified Calendar */}
      {(viewMode === 'day' || viewMode === 'week' || viewMode === 'month') && (
        <Card className="overflow-hidden">
          <Calendar
            view={viewMode}
            date={selectedDate}
            onDateChange={setSelectedDate}
            events={calendarEvents}
            hideViewTabs
            loading={isLoading}
            onEventClick={(e) => {
              const m = e.meta as { booking?: StaffBooking; groupSlotId?: number } | undefined;
              if (m?.groupSlotId != null) {
                setLocation(`/staff/group-classes/${m.groupSlotId}`);
              } else if (m?.booking) {
                handleView(m.booking);
              }
            }}
          />
        </Card>
      )}
    </div>
  );
}
